import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { notificationService, type NotificationView } from '@/database/services/NotificationService'

const PAGE_SIZE = 10

// SSE reconnect backoff: start at 1s, double on each failure up to 30s.
const RECONNECT_BASE_MS = 1_000
const RECONNECT_MAX_MS = 30_000

export interface NotificationListParams {
  limit?: number
  filter?: 'all' | 'unread'
}

export const useNotificationsStore = defineStore('notifications', () => {
  const items = ref<NotificationView[]>([])
  const unreadCount = ref(0)
  // Grand total (All tab) and the unread total (Unread tab) — the count line
  // picks whichever matches the active filter.
  const total = ref(0)
  const loading = ref(false)
  // Separate from `loading` so "load older" shows a button spinner without
  // flipping the whole list back to the skeleton state.
  const loadingMore = ref(false)

  // Pagination state for "load older". `filter` is remembered so loadMore
  // requests the next page of the same filter the list was opened with.
  const page = ref(1)
  const limit = ref(PAGE_SIZE)
  const activeFilter = ref<'all' | 'unread'>('all')

  // Realtime (SSE) connection state. `connected` is exposed so the UI can show
  // a live indicator if desired; the EventSource itself and the reconnect timer
  // are kept out of reactive state.
  const connected = ref(false)
  let eventSource: EventSource | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let reconnectDelay = RECONNECT_BASE_MS
  // Set true by disconnect() so a deliberate close doesn't trigger a reconnect.
  let closedByClient = false

  // Prepend a notification pushed over SSE. De-dupes by _id (the same row could
  // arrive via both the stream and a list fetch) and only bumps the unread
  // badge for genuinely new, unread notifications.
  const handleIncoming = (n: NotificationView) => {
    if (!n?._id || items.value.some(x => x._id === n._id)) return
    items.value = [n, ...items.value]
    total.value += 1
    if (!n.read_at) unreadCount.value += 1
  }

  // EventSource handlers are declared as named, stable references (not inline
  // arrows) so they can be removed in disconnect() — required by the
  // listeners/no-inline-function-event-listener +
  // listeners/no-missing-remove-event-listener lint rules.
  function onSseOpen() {
    connected.value = true
    reconnectDelay = RECONNECT_BASE_MS // reset backoff on a successful connect
  }

  // Backend emits new notifications as a named 'notification' event; the 25s
  // 'heartbeat' event is ignored (it only exists to keep the socket alive).
  function onSseNotification(event: MessageEvent) {
    try {
      handleIncoming(JSON.parse(event.data) as NotificationView)
    } catch (e) {
      console.warn('Failed to parse notification SSE payload', e)
    }
  }

  function onSseError() {
    connected.value = false
    teardownSource()
    if (closedByClient) return
    // Backoff reconnect, capped. Each attempt rebuilds the URL with the current token.
    if (reconnectTimer) clearTimeout(reconnectTimer)
    reconnectTimer = setTimeout(connect, reconnectDelay)
    reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS)
  }

  // Detach handlers, close the socket, and clear the ref. Detaching before
  // close satisfies the require-removeEventListener rule and avoids leaking
  // listeners across reconnects.
  function teardownSource() {
    if (!eventSource) return
    eventSource.removeEventListener('open', onSseOpen)
    eventSource.removeEventListener('notification', onSseNotification)
    eventSource.removeEventListener('error', onSseError)
    eventSource.close()
    eventSource = null
  }

  // More pages exist when we've loaded fewer rows than the relevant total.
  const loadedTotal = computed(() => (activeFilter.value === 'unread' ? unreadCount.value : total.value))
  // Rows already loaded that still count toward the active filter. On the Unread
  // tab a row marked read stays in `items` (the drawer hides it) but no longer
  // counts as loaded-unread, and unreadCount is decremented too — so comparing
  // raw items.length against unreadCount would wrongly hide "Load older" once
  // enough loaded rows were read (e.g. 10 loaded of 15 unread, mark 5 read →
  // 10 < 10 is false, yet 5 older unread are still unfetched). Count only the
  // still-unread loaded rows on that tab.
  const loadedForFilter = computed(() => (activeFilter.value === 'unread' ? items.value.filter(n => !n.read_at).length : items.value.length))
  const hasMore = computed(() => loadedForFilter.value < loadedTotal.value)

  // First page (or filter switch): replace the list.
  const fetch = async (params: NotificationListParams = {}) => {
    loading.value = true
    try {
      activeFilter.value = params.filter ?? 'all'
      limit.value = params.limit ?? PAGE_SIZE
      page.value = 1
      const response = await notificationService.list({
        page: 1,
        limit: limit.value,
        ...(activeFilter.value === 'unread' ? { read: false } : {}),
      })
      items.value = response.data
      total.value = response.total
      unreadCount.value = response.unread_count
      return response
    } finally {
      loading.value = false
    }
  }

  // Append the next page, de-duping by _id (offset pagination can re-surface a
  // boundary row if new notifications arrived since the first page was loaded).
  const loadMore = async () => {
    if (loading.value || loadingMore.value || !hasMore.value) return
    loadingMore.value = true
    try {
      const next = page.value + 1
      const response = await notificationService.list({
        page: next,
        limit: limit.value,
        ...(activeFilter.value === 'unread' ? { read: false } : {}),
      })
      const seen = new Set(items.value.map(n => n._id))
      items.value = [...items.value, ...response.data.filter(n => !seen.has(n._id))]
      page.value = next
      total.value = response.total
      // Don't resync unreadCount here: loadMore is a paging call, and its
      // response carries a snapshot from when the server handled it. Overwriting
      // would clobber an optimistic markRead decrement made while this page was
      // in flight (badge jumps back up until the next sync). fetch() and the
      // optimistic markRead/markAllRead paths own unreadCount.
      return response
    } finally {
      loadingMore.value = false
    }
  }

  // Lightweight: refresh only the unread badge count (one cheap query), without
  // pulling the full list. Used by the top-bar bell on mount.
  const fetchUnreadCount = async () => {
    try {
      const { unread_count } = await notificationService.unreadCount()
      unreadCount.value = unread_count
    } catch (e) {
      // Non-critical: a failed badge fetch must not surface as an unhandled
      // rejection. Leave the current count as-is; SSE / the next fetch corrects it.
      console.warn('Failed to fetch unread notification count', e)
    }
  }

  const markRead = async (id: string) => {
    const n = items.value.find(x => x._id === id)
    if (!n || n.read_at) return
    // Optimistic: flip locally, then persist. Roll back if the request fails.
    n.read_at = new Date().toISOString()
    unreadCount.value = Math.max(0, unreadCount.value - 1)
    try {
      await notificationService.markRead(id)
    } catch (e) {
      n.read_at = null
      unreadCount.value += 1
      console.warn('Failed to mark notification read', e)
    }
  }

  const markAllRead = async () => {
    // The badge counts unread across ALL pages, but only the first page(s) are
    // loaded — so there can be unread rows the client hasn't fetched. Gate the
    // optimistic local update on the loaded unread rows, but still hit the
    // server whenever the badge is nonzero (otherwise older unread stay unread
    // and the badge never clears).
    if (unreadCount.value === 0) return
    const prevUnreadCount = unreadCount.value
    const previouslyUnread = items.value.filter(n => !n.read_at)
    const now = new Date().toISOString()
    previouslyUnread.forEach(n => {
      n.read_at = now
    })
    unreadCount.value = 0
    try {
      await notificationService.markAllRead()
    } catch (e) {
      previouslyUnread.forEach(n => {
        n.read_at = null
      })
      unreadCount.value = prevUnreadCount
      console.warn('Failed to mark all notifications read', e)
    }
  }

  // Open the realtime SSE connection. Idempotent: a second call while already
  // connected is a no-op. Reconnects automatically with exponential backoff on
  // error (the browser also retries native EventSource, but we rebuild the URL
  // so a refreshed token is picked up).
  const connect = () => {
    if (eventSource || typeof EventSource === 'undefined') return
    const url = notificationService.streamUrl()
    if (!url) return // not authenticated

    closedByClient = false
    eventSource = new EventSource(url)
    eventSource.addEventListener('open', onSseOpen)
    eventSource.addEventListener('notification', onSseNotification)
    eventSource.addEventListener('error', onSseError)
  }

  // Close the SSE connection and cancel any pending reconnect. Called on logout
  // and component teardown.
  const disconnect = () => {
    closedByClient = true
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    teardownSource()
    connected.value = false
    reconnectDelay = RECONNECT_BASE_MS
  }

  /** Reset on logout so a logged-out session never shows stale notifications. */
  const reset = () => {
    disconnect()
    items.value = []
    unreadCount.value = 0
    total.value = 0
    page.value = 1
    activeFilter.value = 'all'
  }

  return {
    items,
    unreadCount,
    total,
    loading,
    loadingMore,
    hasMore,
    connected,
    fetch,
    fetchUnreadCount,
    loadMore,
    markRead,
    markAllRead,
    connect,
    disconnect,
    reset,
  }
})
